import {
	addLoFConfig,
	getRiskConfig,
	saveLoF,
	setLoFConfig,
	setLofIsChanged,
	setRefreshList,
	setTab,
} from '@Actions';
import { countValuesNeedingScore } from '@Components/RiskSetup/templates/countValuesNeedingScore';
import { useLoadCoverage } from '@Components/Category/useComponentCoverage';
import { NavigationPrompt } from '@innovyze/stylovyze';
import { distributeToHundred } from '@Utils/distributeToHundred';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
} from '@mui/material';
import { selectLofStore, selectRiskSetting } from '@Selectors';
import {
	selectUnavailableComponentsForSection,
	selectUnrecognisedValuesForSection,
} from '@Selectors/RiskTemplateApply.selectors';
import { ComponentProps, NamespacedStoreState } from '@Types';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGlobalization } from '@Translations/useGlobalization';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';
import { dropUnavailableComponents } from '@Utils/dropUnavailableComponents';
import { escapeAttributeValue } from '@Utils/cssSelector';
import {
	dismissMissingSummary,
	isMissingSummaryDismissed,
} from '@Utils/templateBanner.storage';
import { MissingComponentsBanner } from '@Components/RiskSetup/templates/MissingComponentsBanner.component';
import { SetupCategoriesSkeleton } from '@Components/RiskSetup/templates/SetupCategoriesSkeleton.component';
import { NoLoFCategories } from './NoLoFCategories';
import { RenderedCategories } from './RenderedCategories';
import { ModelDonut } from '@Components/Category/ModelDonut.component';
import { LoFSetupIntro } from './LoFSetupIntro.component';
import { CategoryWeightBanner } from '@Components/Category/CategoryWeightBanner.component';

export const LoFSetup = ({
	onAddComponent,
	onEditComponent,
}: ComponentProps) => {
	const { t } = useGlobalization();
	const isRiskTemplatesEnabled = useIsRiskTemplatesEnabled();
	const [open, setOpen] = React.useState(false);
	const [newCategoryName, setNewCategoryName] = useState('');
	const [validCategoryName, setValidCategoryName] = useState<string>('');
	const dispatch = useDispatch();
	const history = useHistory();

	const riskSettings = selectRiskSetting();
	useLoadCoverage(riskSettings.configId, isRiskTemplatesEnabled);
	const lofStore = selectLofStore();
	const categories = lofStore?.categories;
	const isLoadingCategories = lofStore?.isLoadingCategories ?? false;
	const unavailable = useSelector((state: NamespacedStoreState) =>
		selectUnavailableComponentsForSection(state, 'lof'),
	);
	const unrecognisedValues = useSelector((state: NamespacedStoreState) =>
		selectUnrecognisedValuesForSection(state, 'lof'),
	);
	const showSkeleton = isLoadingCategories && (categories?.length ?? 0) === 0;
	const [missingSummaryDismissed, setMissingSummaryDismissed] = useState(
		isMissingSummaryDismissed(riskSettings.configId, 'lof'),
	);

	useEffect(() => {
		dispatch(setTab('lof'));
	}, []);

	const handleClose = () => {
		setNewCategoryName('');
		setValidCategoryName('');
		setOpen(false);
	};

	const handleAddCategoryClicked = () => {
		if (!isLoadingCategories) setOpen(true);
	};

	const handleAdd = () => {
		if (newCategoryName !== '') {
			dispatch(
				addLoFConfig({
					name: newCategoryName,
					isCondition: false,
					useThisOnly: false,
					weighting: 0,
					useHighestScore: false,
					components: [],
				}),
			);
			setOpen(false);
			setNewCategoryName('');
			setValidCategoryName('');
		}
	};

	const totalWeight = lofStore?.categories
		?.filter(cat => !cat.useThisOnly)
		?.map(cat => cat.weighting)
		?.reduce((pre, current) => pre + current, 0);

	// One click: scale the category weights proportionally to total 100%
	// (even split if none set yet) — same helper as the components, no
	// mental maths. Keyed by object reference, not name, so two categories
	// that happen to share a name (allowed in the classic path) don't
	// collide and silently drop one.
	const balanceCategories = () => {
		const cats = lofStore?.categories ?? [];
		const targets = cats.filter(c => !c.useThisOnly);
		if (targets.length === 0) return;
		const rounded = distributeToHundred(targets.map(c => c.weighting || 0));
		const weightByCategory = new Map(
			targets.map((c, i) => [c, rounded[i]]),
		);
		dispatch(
			setLoFConfig(
				cats.map(c =>
					weightByCategory.has(c)
						? { ...c, weighting: weightByCategory.get(c) as number }
						: c,
				),
			),
		);
	};

	const validateCategoryName = (event: ChangeEvent<HTMLInputElement>) => {
		const name = event.target.value.toUpperCase();
		let valid = true;
		valid =
			lofStore?.categories.find(
				category => category.name.toUpperCase() == name,
			) === undefined;
		setValidCategoryName(valid ? '' : t('Enter a unique name'));
		setNewCategoryName(valid ? event.target.value : '');
	};

	const isConfigValid = () => {
		const categories = lofStore.categories;
		if (!categories) return false;
		if (categories.length === 0) {
			return false;
		}
		const categoriesTotalWeight = categories
			.filter(category => !category.useThisOnly)
			.map(category => category.weighting)
			.reduce((prev, current) => prev + current, 0);
		if (
			categoriesTotalWeight !== 100 &&
			categories.filter(category => !category.useThisOnly).length > 0
		)
			return false;
		for (const category of categories) {
			if (
				!category.useHighestScore &&
				category.components &&
				category.components.length != 0
			) {
				const total = category.components
					.map(component =>
						component.select ? component.weighting : 0,
					)
					.reduce((prev, current) => prev + current, 0);

				// We want a total of 0 or 100 to be valid
				return total === 0 || total === 100;
			}
		}
		return true;
	};

	const navMsg = isConfigValid()
		? t(
				'You have modified your LOF configuration and have unsaved changes.  You can save your configuration changes, discard changes, or cancel to continue editing.',
		  )
		: t(
				'You have modified your LOF configuration and have unsaved changes.  You can discard changes, or cancel to continue editing.',
		  );

	const refresh = () => {
		dispatch(getRiskConfig(riskSettings.configId));
	};

	const onContinueWithoutMissingComponents = () => {
		const lofDrop = unavailable.map(item => ({
			categoryName: item.categoryName,
			componentName: item.componentName,
		}));
		dispatch(
			setLoFConfig(dropUnavailableComponents(categories ?? [], lofDrop)),
		);
		dispatch(saveLoF({ refresh }));
		dismissMissingSummary(riskSettings.configId, 'lof');
		setMissingSummaryDismissed(true);
	};

	const onReviewMissingComponents = () => {
		const first = unavailable[0];
		if (first) {
			document
				.querySelector(
					`[data-cy="component-${escapeAttributeValue(
						first.componentName,
					)}"][data-category="${escapeAttributeValue(
						first.categoryName,
					)}"]`,
				)
				?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	};

	return (
		<>
			<NavigationPrompt
				when={lofStore.isChanged}
				navigate={nextLocation => {
					history.push(nextLocation);
				}}
				confirmText={isConfigValid() ? t('Save') : t('Discard')}
				onNavigateAway={() => {
					if (isConfigValid()) {
						dispatch(saveLoF({ refresh: refresh }));
						dispatch(setRefreshList(true));
					} else {
						dispatch(getRiskConfig(riskSettings.configId));
						dispatch(setLofIsChanged(false));
					}
				}}
				secondaryConfirmText={
					isConfigValid() ? t('Discard') : undefined
				}
				onSecondaryNavigateAway={() => {
					dispatch(getRiskConfig(riskSettings.configId));
					dispatch(setLofIsChanged(false));
				}}
				shouldBlockNavigation={() => true}
				shouldBlockReload={lofStore.isChanged}
				content={navMsg}></NavigationPrompt>
			{isRiskTemplatesEnabled && <LoFSetupIntro />}
			{isRiskTemplatesEnabled &&
				!showSkeleton &&
				categories.some(c => !c.useThisOnly) && (
					<CategoryWeightBanner
						totalWeight={totalWeight ?? 0}
						onBalance={balanceCategories}
					/>
				)}
			<Grid container {...(isRiskTemplatesEnabled ? { spacing: 3 } : {})}>
				<Grid
					item
					xs={12}
					{...(isRiskTemplatesEnabled ? { md: 8, lg: 9 } : {})}
					container>
					{showSkeleton ? (
						<SetupCategoriesSkeleton />
					) : (
						<>
							{isRiskTemplatesEnabled &&
								!missingSummaryDismissed &&
								(unavailable.length > 0 ||
									unrecognisedValues.length > 0) && (
									<Grid item xs={12}>
										<MissingComponentsBanner
											section="lof"
											unavailableComponents={unavailable}
											unrecognisedValues={
												unrecognisedValues
											}
											valuesNeedingScore={countValuesNeedingScore(
												categories,
												unrecognisedValues,
											)}
											onContinue={
												onContinueWithoutMissingComponents
											}
											onReview={onReviewMissingComponents}
											onDismiss={() =>
												setMissingSummaryDismissed(true)
											}
											configId={riskSettings.configId}
										/>
									</Grid>
								)}
							{categories.length === 0 && (
								<NoLoFCategories
									handleAddCategoryClicked={
										handleAddCategoryClicked
									}
								/>
							)}
							<RenderedCategories
								isLoading={isLoadingCategories}
								categories={categories}
								totalWeight={totalWeight ?? 0}
								onAddComponent={onAddComponent}
								onEditComponent={onEditComponent}
								handleAddCategoryClicked={
									handleAddCategoryClicked
								}
							/>
						</>
					)}
				</Grid>
				{isRiskTemplatesEnabled ? (
					!showSkeleton &&
					categories.length > 0 && (
						<Grid item xs={12} md={4} lg={3}>
							<Box sx={{ position: 'sticky', top: 16 }}>
								<ModelDonut
									categories={categories}
									axisLabel={t('LoF')}
								/>
							</Box>
						</Grid>
					)
				) : (
					<Grid xs={4} item container>
						{/* <FieldInspectionSummary /> */}
					</Grid>
				)}
			</Grid>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby="form-dialog-title">
				<DialogTitle id="form-dialog-title" style={{ width: '481px' }}>
					{t('Add Category')}
				</DialogTitle>
				<DialogContent>
					<TextField
						margin="dense"
						id="name"
						label={t('Category Name')}
						type="text"
						fullWidth
						variant="outlined"
						defaultValue={newCategoryName}
						helperText={validCategoryName}
						error={validCategoryName !== ''}
						onChange={validateCategoryName}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						variant="contained">
						{t('Cancel')}
					</Button>
					<Button
						onClick={handleAdd}
						color="primary"
						variant="contained">
						{t('Add Category')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
